import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { Search } from "lucide-react";
import sh from "bundles/common/utils/sh";
import { CircleUnchecked, CircleChecked } from "../common/CheckIcons";
import UserAvatar from "../common/UserAvatar";

const CreatePipelineDrawer = ({ open, onClose, onCreated, onUpdated, editingPipeline }) => {
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const isEditing = Boolean(editingPipeline);

  const [pipelineName, setPipelineName] = useState("");
  const [linkedOffers, setLinkedOffers] = useState([]);
  const [offerSearch, setOfferSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [brief, setBrief] = useState("");
  const [visibility] = useState("shared");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef(null);

  const [allUsers, setAllUsers] = useState([]);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [userSearch, setUserSearch] = useState("");

  const searchOffers = useCallback((query) => {
    if (query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    setLoadingSuggestions(true);
    sh.get("/internal/search/job_offers", { params: { q: query.trim() } })
      .then((res) => setSuggestions(res.data || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoadingSuggestions(false));
  }, []);

  useEffect(() => {
    if (!open || allUsers.length > 0) return;
    setFetchingUsers(true);
    sh.get("/internal/search/internal_users", { params: { include_coordinators: true } })
      .then((res) => setAllUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAllUsers([]))
      .finally(() => setFetchingUsers(false));
  }, [open, allUsers.length]);

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return allUsers;
    const q = userSearch.toLowerCase();
    return allUsers.filter(
      (u) => (u.full_name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q),
    );
  }, [allUsers, userSearch]);

  const groupedByZone = useMemo(() => {
    const zones = { francophone: [], flemish: [] };
    filteredUsers.forEach((u) => {
      if (zones[u.market_zone]) zones[u.market_zone].push(u);
    });
    return zones;
  }, [filteredUsers]);

  const toggleUserId = useCallback((id) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleZone = useCallback((zone) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      const zoneIds = (groupedByZone[zone] || []).map((u) => u.id);
      const allSelected = zoneIds.every((id) => next.has(id));
      zoneIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }, [groupedByZone]);

  const resetForm = useCallback(() => {
    setPipelineName("");
    setLinkedOffers([]);
    setOfferSearch("");
    setShowSuggestions(false);
    setSuggestions([]);
    setBrief("");
    setError("");
    setSelectedUserIds(new Set());
    setUserSearch("");
    clearTimeout(debounceRef.current);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    resetForm();
  }, [onClose, resetForm]);

  const handleCreate = useCallback(() => {
    if (!pipelineName.trim() || creating) return;

    setCreating(true);
    setError("");

    let request;

    if (isEditing) {
      request = sh.put(`/internal/profile_pipelines/${editingPipeline.id}`, {
        authenticity_token,
        profile_pipeline: {
          title: pipelineName.trim(),
          content: brief.trim(),
          job_offer_ids: linkedOffers.map((o) => o.id),
        },
      });
    } else {
      const payload = {
        title: pipelineName.trim(),
        content: brief.trim(),
        pipeline_type: visibility,
        job_offer_ids: linkedOffers.map((o) => o.id),
      };
      if (visibility === "shared" && selectedUserIds.size > 0) {
        payload.assigned_internal_user_ids = [...selectedUserIds];
      }
      request = sh.post("/internal/profile_pipelines", {
        authenticity_token,
        profile_pipeline: payload,
      });
    }

    request
      .then((res) => {
        if (isEditing) onUpdated?.(res.data);
        else onCreated?.();
        handleClose();
      })
      .catch((err) => {
        const message = err?.response?.data?.errors?.join(", ") || `Could not ${isEditing ? "update" : "create"} pipeline.`;
        setError(message);
      })
      .finally(() => setCreating(false));
  }, [authenticity_token, brief, creating, editingPipeline, handleClose, isEditing, linkedOffers, onCreated, onUpdated, pipelineName, selectedUserIds, visibility]);

  useEffect(() => {
    if (open && editingPipeline) {
      setPipelineName(editingPipeline.title || "");
      setLinkedOffers(
        (editingPipeline.job_offers || []).map((jo) => ({
          id: jo.id,
          title: jo.title,
          company: jo.company_name,
        })),
      );
      setBrief(editingPipeline.content || "");
      setError("");
    }
  }, [open, editingPipeline]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 448 },
          maxWidth: "100%",
          borderLeft: "1px solid hsl(140 8% 88%)",
          backgroundColor: "#ffffff",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid hsl(140 8% 88%)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 16, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
            {isEditing ? "Edit pipeline" : "New pipeline"}
          </Typography>
          <IconButton size="small" onClick={handleClose} sx={{ color: "hsl(150 10% 46%)", "&:hover": { backgroundColor: "hsl(140 8% 90%)" } }}>
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Pipeline title */}
          <Box>
            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", mb: 0.75 }}>
              Pipeline title {" "}
              <Box component="span" sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", opacity: 0.7 }}>
                
              </Box>
            </Typography>

            {/* Pipeline name input */}
            <Box
              component="input"
              placeholder="Pipeline name"
              value={pipelineName}
              onChange={(e) => setPipelineName(e.target.value)}
              sx={{
                width: "100%",
                height: 40,
                borderRadius: "12px",
                border: "1px solid hsl(140 8% 88%)",
                backgroundColor: "hsl(140 10% 95%)",
                px: 1.5,
                py: 1,
                fontSize: 14,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
                outline: "none",
                mb: 1,
                "&::placeholder": { color: "hsl(150 10% 46%)" },
                "&:focus": {
                  outline: "2px solid hsl(150 38% 17%)",
                  outlineOffset: "2px",
                },
              }}
            />

            {/* Offer search input */}
            <Box sx={{ position: "relative" }}>
              <Box
                component="input"
                placeholder="Search job offers to link..."
                value={offerSearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setOfferSearch(val);
                  setShowSuggestions(true);
                  clearTimeout(debounceRef.current);
                  debounceRef.current = setTimeout(() => searchOffers(val), 300);
                }}
                onFocus={() => offerSearch.trim().length >= 3 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                sx={{
                  width: "100%",
                  height: 40,
                  borderRadius: "12px",
                  border: "1px solid hsl(140 8% 88%)",
                  backgroundColor: "hsl(140 10% 95%)",
                  px: 1.5,
                  py: 1,
                  fontSize: 14,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(150 30% 12%)",
                  outline: "none",
                  "&::placeholder": { color: "hsl(150 10% 46%)" },
                  "&:focus": {
                    outline: "2px solid hsl(150 38% 17%)",
                    outlineOffset: "2px",
                  },
                }}
              />

              {showSuggestions && offerSearch.trim().length >= 3 && (suggestions.length > 0 || loadingSuggestions) && (
                <Paper
                  elevation={4}
                  sx={{
                    position: "absolute",
                    zIndex: 20,
                    top: "100%",
                    mt: 0.5,
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid hsl(140 8% 88%)",
                    overflow: "hidden",
                  }}
                >
                  <Typography sx={{ px: 1.5, py: 0.75, fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", borderBottom: "1px solid hsl(140 8% 88%)" }}>
                    {loadingSuggestions ? "Searching..." : "Squarehub matches"}
                  </Typography>
                  {loadingSuggestions && suggestions.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={18} sx={{ color: "hsl(150 38% 17%)" }} />
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 192, overflowY: "auto" }}>
                      {suggestions
                        .filter((offer) => !linkedOffers.some((lo) => lo.id === offer.id))
                        .map((offer) => (
                          <Box
                            key={offer.id}
                            component="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setLinkedOffers((prev) => [...prev, offer]);
                              setOfferSearch("");
                              setSuggestions([]);
                              setShowSuggestions(false);
                              if (!pipelineName.trim()) setPipelineName(offer.title);
                              if (!brief.trim() && linkedOffers.length === 0) {
                                const sections = [
                                  offer.description && `${offer.description}`,
                                  offer.responsibilities && `Responsibilities:\n${offer.responsibilities}`,
                                  offer.profile && `Profile:\n${offer.profile}`,
                                  offer.offer && `Offer:\n${offer.offer}`,
                                ].filter(Boolean);
                                if (sections.length > 0) setBrief(sections.join("\n\n"));
                              }
                            }}
                            sx={{
                              width: "100%",
                              textAlign: "left",
                              border: "none",
                              backgroundColor: "transparent",
                              cursor: "pointer",
                              px: 1.5,
                              py: 1,
                              display: "block",
                              transition: "background-color 0.15s ease",
                              "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.5)" },
                            }}
                          >
                            <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                              {offer.title}
                            </Typography>
                            {offer.company && (
                              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
                                {offer.company}
                              </Typography>
                            )}
                          </Box>
                        ))}
                    </Box>
                  )}
                </Paper>
              )}
            </Box>

            {/* Linked offers pills */}
            {linkedOffers.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                {linkedOffers.map((offer) => (
                  <Box
                    key={offer.id}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: "16px",
                      backgroundColor: "hsla(150, 38%, 17%, 0.08)",
                      border: "1px solid hsla(150, 38%, 17%, 0.15)",
                      maxWidth: "calc((100% - 6px) / 2)",
                      minWidth: 0,
                    }}
                  >
                    <LinkRoundedIcon sx={{ fontSize: 12, color: "hsl(142 50% 25%)" }} />
                    <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", maxWidth: 200 }} noWrap>
                      {offer.title}{offer.company ? ` – ${offer.company}` : ""}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setLinkedOffers((prev) => prev.filter((o) => o.id !== offer.id))}
                      sx={{ p: 0.25, ml: 0.25, "&:hover": { backgroundColor: "hsl(140 8% 90%)" } }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Brief */}
          <Box>
            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", mb: 0.75 }}>
              Brief
            </Typography>
            <Box
              component="textarea"
              placeholder="Job requirements, salary range, must-haves..."
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              sx={{
                width: "100%",
                minHeight: 160,
                borderRadius: "12px",
                border: "1px solid hsl(140 8% 88%)",
                backgroundColor: "hsl(140 10% 95%)",
                px: 1.5,
                py: 1,
                fontSize: 14,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
                outline: "none",
                resize: "vertical",
                "&::placeholder": { color: "hsl(150 10% 46%)" },
                "&:focus": {
                  outline: "2px solid hsl(150 38% 17%)",
                  outlineOffset: "2px",
                },
              }}
            />
          </Box>

          {!isEditing && visibility === "shared" && (
            <Box>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", mb: 0.75 }}>
                Invite users
              </Typography>
              <Box sx={{ position: "relative", mb: 1 }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    pointerEvents: "none",
                    color: "hsl(150 10% 46%)",
                  }}
                >
                  <Search size={14} strokeWidth={2} />
                </Box>
                <Box
                  component="input"
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  sx={{
                    width: "100%",
                    height: 40,
                    borderRadius: "12px",
                    border: "1px solid hsl(140 8% 88%)",
                    backgroundColor: "hsl(140 10% 95%)",
                    pl: 4,
                    pr: 1.5,
                    py: 1,
                    fontSize: 14,
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    color: "hsl(150 30% 12%)",
                    outline: "none",
                    "&::placeholder": { color: "hsl(150 10% 46%)" },
                    "&:focus": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: "2px" },
                  }}
                />
              </Box>
              <Box sx={{ maxHeight: 400, overflowY: "auto", mx: -0.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                {fetchingUsers ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: "hsl(150 38% 17%)" }} />
                  </Box>
                ) : filteredUsers.length === 0 ? (
                  <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)", textAlign: "center", py: 4, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    No users found
                  </Typography>
                ) : (
                  Object.entries(groupedByZone)
                    .filter(([, users]) => users.length > 0)
                    .map(([zone, users]) => {
                      const zoneIds = users.map((u) => u.id);
                      const allZoneSelected = zoneIds.every((id) => selectedUserIds.has(id));
                      const someZoneSelected = !allZoneSelected && zoneIds.some((id) => selectedUserIds.has(id));
                      const zoneLabel = zone === "francophone" ? "Francophone" : "Flemish";

                      return (
                        <Box key={zone}>
                          {/* Zone header */}
                          <Box
                            onClick={() => toggleZone(zone)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              px: 1.5,
                              py: 0.75,
                              cursor: "pointer",
                              borderRadius: "8px",
                              transition: "background-color 0.15s ease",
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                            }}
                          >
                            <Checkbox
                              checked={allZoneSelected}
                              indeterminate={someZoneSelected}
                              disableRipple
                              icon={<CircleUnchecked />}
                              checkedIcon={<CircleChecked />}
                              indeterminateIcon={
                                <Box
                                  sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: "50%",
                                    backgroundColor: "hsl(150 38% 17%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  <Box sx={{ width: 8, height: 2, backgroundColor: "hsl(80 97% 72%)", borderRadius: 1 }} />
                                </Box>
                              }
                              sx={{ p: 0 }}
                              tabIndex={-1}
                            />
                            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {zoneLabel} team
                            </Typography>
                            <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", ml: "auto" }}>
                              {zoneIds.filter((id) => selectedUserIds.has(id)).length}/{zoneIds.length}
                            </Typography>
                          </Box>

                          {/* Zone users */}
                          {users.map((u) => {
                            const checked = selectedUserIds.has(u.id);
                            return (
                              <Box
                                key={u.id}
                                onClick={() => toggleUserId(u.id)}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  pl: 4.5,
                                  pr: 1.5,
                                  py: 0.875,
                                  borderRadius: "10px",
                                  cursor: "pointer",
                                  transition: "background-color 0.15s ease",
                                  backgroundColor: checked ? "hsla(150, 38%, 17%, 0.06)" : "transparent",
                                  "&:hover": { backgroundColor: checked ? "hsla(150, 38%, 17%, 0.09)" : "rgba(0,0,0,0.02)" },
                                }}
                              >
                                <Checkbox
                                  checked={checked}
                                  disableRipple
                                  icon={<CircleUnchecked />}
                                  checkedIcon={<CircleChecked />}
                                  sx={{ p: 0, "&:hover .MuiBox-root": { borderColor: "hsl(150 38% 17%)" } }}
                                  tabIndex={-1}
                                />
                                <UserAvatar user={u} size={28} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.3 }} noWrap>
                                    {u.full_name || u.email}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", lineHeight: 1.3 }} noWrap>
                                    {u.email}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })
                )}
              </Box>
              {selectedUserIds.size > 0 && (
                <Typography sx={{ mt: 0.75, fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
                  {selectedUserIds.size} member{selectedUserIds.size !== 1 ? "s" : ""} will be assigned
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 2, borderTop: "1px solid hsl(140 8% 88%)" }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleCreate}
            disabled={!pipelineName.trim() || creating}
            sx={{
              height: 40,
              borderRadius: "12px",
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              "&:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.9)" },
              "&.Mui-disabled": {
                opacity: 0.5,
                backgroundColor: "hsl(150 38% 17%)",
                color: "hsl(80 97% 72%)",
                pointerEvents: "none",
              },
            }}
          >
            {creating ? (isEditing ? "Saving..." : "Creating...") : (isEditing ? "Save changes" : "Create pipeline")}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CreatePipelineDrawer;
